import React, {useCallback, useEffect, useState} from 'react'
import Swal from 'sweetalert2'
import {toast} from 'react-toastify'
import {KTIcon} from '../../../../_metronic/helpers'
import {LoadingData} from '../../../../components/LoadingData'
import {ModeleSavReferentiel, SavReferentielType} from '../../../../components/sav/Models'
import {
  deleteSavReferentiel,
  getSavReferentielAdmin,
  savErreur,
} from '../../../../components/sav/requests'
import {
  SAV_COMPORTEMENT_LIBELLES,
  SAV_COMPORTEMENT_OPTIONS,
} from '../../../../components/sav/libelles'
import SavReferentielModal from './SavReferentielModal'

type Props = {
  type: SavReferentielType
  ajouter: string
  aide: string
}

// Onglet de liste d'un référentiel SAV (écran 10.3)
const SavReferentielTab = ({type, ajouter, aide}: Props) => {
  const [loading, setLoading] = useState(false)
  const [valeurs, setValeurs] = useState<Array<ModeleSavReferentiel>>([])
  const [utilises, setUtilises] = useState<Array<number>>([])
  const [comportements, setComportements] = useState<Array<string>>([])
  // null : modale fermée ; objet sans id : création
  const [edition, setEdition] = useState<ModeleSavReferentiel | null>(null)

  const charger = useCallback(() => {
    setLoading(true)
    getSavReferentielAdmin(type)
      .then((reponse) => {
        setValeurs(reponse.data)
        setUtilises(reponse.utilises)
        setComportements(reponse.comportements)
      })
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setLoading(false))
  }, [type])

  useEffect(() => {
    charger()
  }, [charger])

  const option = (valeur: ModeleSavReferentiel) =>
    type === 'motifs'
      ? `Retour requis par défaut : ${valeur.retourParDefaut ? 'oui' : 'non'}`
      : SAV_COMPORTEMENT_OPTIONS[valeur.comportement ?? ''] ?? '—'

  const supprimer = (valeur: ModeleSavReferentiel) => {
    Swal.fire({
      title: 'Attention',
      text: `Supprimer « ${valeur.libelle} » ?`,
      icon: 'warning',
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      showCancelButton: true,
      confirmButtonColor: '#1BC5BD',
      cancelButtonColor: '#F64E60',
    }).then((choix) => {
      if (!choix.isConfirmed || !valeur.id) return
      deleteSavReferentiel(type, valeur.id)
        .then(() => {
          toast.success('Valeur supprimée')
          charger()
        })
        .catch((e) => toast.error(savErreur(e)))
    })
  }

  return (
    <>
      <div className='d-flex justify-content-between align-items-center mb-5'>
        <div className='text-muted'>{aide}</div>
        <button className='btn btn-sm btn-success' onClick={() => setEdition({libelle: ''})}>
          <KTIcon iconName='plus' className='fs-3' />
          {ajouter}
        </button>
      </div>

      {loading ? (
        <LoadingData />
      ) : (
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th className='w-75px'>Ordre</th>
                <th className='min-w-150px'>Libellé</th>
                <th className='min-w-150px'>Comportement dans KOENA</th>
                <th className='min-w-200px'>Option</th>
                <th className='w-100px'>État</th>
                <th className='w-100px text-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {valeurs.length ? (
                valeurs.map((valeur) => (
                  <tr key={valeur.id}>
                    <td>{valeur.ordre}</td>
                    <td className='fw-bold'>{valeur.libelle}</td>
                    <td>
                      <span className='badge badge-light-primary'>
                        {type === 'motifs'
                          ? 'Motif'
                          : SAV_COMPORTEMENT_LIBELLES[valeur.comportement ?? ''] ??
                            valeur.comportement}
                      </span>
                    </td>
                    <td className='text-muted'>{option(valeur)}</td>
                    <td>
                      {valeur.actif ? (
                        <span className='badge badge-light-success'>Actif</span>
                      ) : (
                        <span className='badge badge-light-danger'>Inactif</span>
                      )}
                    </td>
                    <td>
                      <div className='d-flex justify-content-end flex-shrink-0'>
                        <button
                          className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                          title='Modifier'
                          onClick={() => setEdition(valeur)}
                        >
                          <i className='bi bi-pencil-square fs-2'></i>
                        </button>
                        {valeur.id && !utilises.includes(valeur.id) && (
                          <button
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                            title='Supprimer'
                            onClick={() => supprimer(valeur)}
                          >
                            <i className='bi bi-trash fs-2'></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className='text-center'>
                    Aucune valeur
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className='alert alert-primary mt-5 mb-0'>
        Une valeur déjà utilisée dans un dossier ne se supprime pas : elle se désactive, pour
        conserver l'historique. Le comportement est choisi dans une liste fixe, car il pilote les
        règles de KOENA.
      </div>

      {edition && (
        <SavReferentielModal
          type={type}
          titre={edition.id ? edition.libelle : ajouter}
          valeur={edition.id ? edition : null}
          comportements={comportements}
          onClose={() => setEdition(null)}
          onSaved={() => {
            setEdition(null)
            charger()
          }}
        />
      )}
    </>
  )
}

export default SavReferentielTab
